import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useSWRMutation from "swr/mutation";
import { Box, Container } from "@mui/material";
import AddEditFormCst from "@components/AddEditFormCst";
import AnimationFadeIn from "@components/AnimationFadeIn";
import BackgroundImageCst from "@components/BackgroundImageCst";
import Breadcrumb from "@components/Breadcrumb";
import HeroCst from "@components/HeroCst";
import ModalConfirmsCst from "@components/ModalConfirmsCst";
import { StatusEnum } from "@components/ModalConfirmsCst/types";
import { useFetchDetail } from "@hooks/useFetchDetail";
import { ServicesURL } from "@utils/utilsApi";
import { MethodType } from "src/services/type";
import { PagesID } from "@utils/utilsConfigurations";
import { axiosInstance, fetcher } from "src/services/config";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

const AddPlan: React.FC = () => {
  const { t } = useTranslation(["manage-plans-page"]);
  const { id } = useParams();
  const navigate = useNavigate();
  const [error, showError] = useState(false);

  const [funds, setFunds] = useState([]);
  const [openToast, setOpenToast] = useState<{
    s: StatusEnum;
    message: string;
    description?: string;
  }>();

  const { columns, dataDetail, mutate } = useFetchDetail(
    undefined,
    undefined,
    PagesID["corporate.foundedTraining.managePlan.plan.add"],
  );

  const { trigger: triggerAddPlan } = useSWRMutation(
    `${ServicesURL.addPlan.replace(":corporateId", id || "")}`,
    (url: string, arg: { arg: any }) => {
      return fetcher<any>(`${url}`, MethodType.POST, { ...arg.arg });
    },
  );

  const { trigger: getFunds } = useSWRMutation(
    `${ServicesURL.fund}`,
    (url: string) => {
      return () => axiosInstance.get(`${url}`);
    },
  );

  useEffect(() => {
    getFunds().then((resp: any) => {
      const result = resp.data.map((el: any) => {
        const { fund, logo_encoded } = el;
        const newEl = {
          value: el.id,
          label: el.fund,
          fund: el.catalog_association_type,
          logo_encoded,
        };
        return newEl;
      });

      setFunds(result);
    });
  }, []);

  const inputsException = (col: any, inputForm: any) => {
    if (col.accessorKey === "id_ol") {
      col.required = false;
      return {};
    }
    if (col.accessorKey === "id") {
      col.required = false;
      return {};
    }

    const startDate = inputForm.start_date
      ? new Date(inputForm?.start_date["$d"])
      : "";
    const endDate = inputForm.end_date
      ? new Date(inputForm?.end_date["$d"])
      : "";
    const formatStart = (
      dayjs(new Date(startDate)).format("YYYY-MM-DD") || ""
    ).replace("-", "");
    const formatEnd = (
      dayjs(new Date(endDate)).format("YYYY-MM-DD") || ""
    ).replace("-", "");
    if (
      formatStart &&
      formatEnd &&
      formatStart > formatEnd &&
      (col.accessorKey === "start_date" || col.accessorKey === "end_date")
    ) {
      col.required = true;
      col.error = "start_end_date";
      showError(true);
    } else if (
      formatStart &&
      formatEnd &&
      formatStart <= formatEnd &&
      (col.accessorKey === "start_date" || col.accessorKey === "end_date")
    ) {
      showError(false);
    }

    return col;
  };

  const onSubmit = async (inputForm: any) => {
    if (!error) {
      const startDate = new Date(inputForm.start_date["$d"]);
      const endDate = new Date(inputForm.end_date["$d"]);
      const objToSend = {
        ...inputForm,
        start_date: dayjs(new Date(startDate)).format("YYYY-MM-DD") || "",
        end_date: dayjs(new Date(endDate)).format("YYYY-MM-DD") || "",
        fund: {
          id: inputForm.fund.value,
          fund: inputForm.fund.catalog_association_type,
          catalog_association_type: inputForm.fund.fund,
          logo_encoded: inputForm.fund.logo_encoded,
        },
      };
      setOpenToast({ s: StatusEnum.LOADING, message: "loading" });
      try {
        await triggerAddPlan(objToSend);
        setOpenToast({ s: StatusEnum.OK, message: "" });
      } catch (error) {
        setOpenToast(undefined);
      }
      mutate && mutate(objToSend);
    }
  };

  return (
    <AnimationFadeIn>
      <BackgroundImageCst
        customWidth={"100%"}
        customHeight={"603px"}
        image="section-skills-capabilities.jpg"
        position={"absolute"}
        fullpage={true}
      />
      <Breadcrumb />
      <Container
        maxWidth={"xxl"}
        sx={{
          position: "relative",
          zIndex: 1,
        }}
      >
        <HeroCst title={t("add.hero-title")} description={""} />
        {columns && (
          <Box
            sx={{
              mt: "25px",
            }}
          >
            <AddEditFormCst
              row={dataDetail || []}
              columns={columns}
              onSubmit={onSubmit}
              actionsForForms={[]}
              backButton={() => {
                navigate(-1);
              }}
              inputsException={inputsException}
              optionsSelect={funds}
            />
          </Box>
        )}
      </Container>
      <ModalConfirmsCst
        open={!!openToast}
        title={""}
        description={""}
        onCallBackClosed={() => {
          setOpenToast(undefined);
        }}
        status={openToast}
      />
    </AnimationFadeIn>
  );
};

export default AddPlan;
